/**
 * Text selection service — reads the currently selected text from the
 * live Word document via Office JS.
 *
 * SRP: Only reads selection state. No document modification, no UI.
 * DIP: Consumers depend on the TextSelection interface, not on
 *      Office JS internals.
 */

import { detectCursorSection } from "./wordWriter";

export interface TextSelection {
  /** The selected text content (may be empty if no selection). */
  text: string;
  /** The section number the selection is in, or null if not in a section. */
  sectionNumber: string | null;
}

/**
 * Read the current text selection from the Word document.
 *
 * Returns null if Office JS is not available (e.g. running outside Word).
 * Returns an empty text if the cursor is placed but no text is selected.
 */
export async function getSelectedText(): Promise<TextSelection | null> {
  if (typeof Office === "undefined" || typeof Word === "undefined" || !Office.context) {
    return null;
  }

  try {
    const text = await Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.load("text");
      await context.sync();
      return selection.text?.trim() || "";
    });

    // Get section context from cursor position
    const cursorInfo = await detectCursorSection();

    return {
      text,
      sectionNumber: cursorInfo?.sectionNumber || null,
    };
  } catch {
    return null;
  }
}

/**
 * The paragraph the cursor sits in — its text and section — or null outside
 * Word. Used to place copied text right after it.
 */
export async function getCursorParagraph(): Promise<TextSelection | null> {
  if (typeof Office === "undefined" || typeof Word === "undefined" || !Office.context) {
    return null;
  }
  try {
    const text = await Word.run(async (context) => {
      const para = context.document.getSelection().paragraphs.getFirst();
      para.load("text");
      await context.sync();
      return para.text?.trim() || "";
    });
    const cursorInfo = await detectCursorSection();
    return { text, sectionNumber: cursorInfo?.sectionNumber || null };
  } catch {
    return null;
  }
}
